import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  Tooltip,
  Alert,
  CircularProgress,
  Snackbar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
} from '@mui/material';
import {
  ArrowBack,
  Download,
  AttachFile,
  Warning,
  CheckCircle,
  Business,
  Person,
  Email,
  Phone,
  LocationOn,
  Today,
  Inventory,
  Category,
  Assignment,
  Description,
  Refresh,
  ExpandMore,
  Comment,
} from '@mui/icons-material';
import { useAuth } from 'react-oidc-context';
import appConfig from '../config/app';

// API service functions
const createApiService = (auth) => ({
  async getRMADetails(rmaId) {
    const token = auth.user?.access_token;
    const cognito_user_id = auth.user?.profile?.sub;

    const response = await fetch(`${appConfig.api.baseUrl}/rma/${rmaId}/details/?detailed=true`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Cognito-User-ID': cognito_user_id,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('RMA Details:', data); // Debug API response
    return data;
  },

// REPLACE WITH THIS:
async downloadAttachment(attachmentId) {
  const token = auth.user?.access_token;
  const cognito_user_id = auth.user?.profile?.sub;

  const response = await fetch(`${appConfig.api.baseUrl}/rma/attachments/${attachmentId}/download/`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'X-Cognito-User-ID': cognito_user_id,
    },
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    const json = await response.json();
    if (json.download_url) {
      return { type: 'redirect', url: json.download_url, filename: json.filename };
    }
    throw new Error(json.error || json.detail || 'Unexpected JSON response');
  }

  const blob = await response.blob();
  return { type: 'blob', blob };
},

  async exportRMA(rmaId) {
    const token = auth.user?.access_token;
    const cognito_user_id = auth.user?.profile?.sub;

    const response = await fetch(`${appConfig.api.baseUrl}/rma/${rmaId}/export/`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-Cognito-User-ID': cognito_user_id,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.blob();
  },

  async respondToFieldIssue(issueId, response) {
    const token = auth.user?.access_token;
    const cognito_user_id = auth.user?.profile?.sub;

    console.log('Responding to issue:', { issueId, response }); // Debug request
    const apiResponse = await fetch(`${appConfig.api.baseUrl}/rma/field-issues/${issueId}/respond/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Cognito-User-ID': cognito_user_id,
      },
      body: JSON.stringify({
        customer_response: response,
      }),
    });

    if (!apiResponse.ok) {
      const errorData = await apiResponse.json();
      console.error('Response error:', errorData); // Debug error
      throw new Error(`HTTP error! status: ${apiResponse.status}, message: ${errorData.error || 'Unknown error'}`);
    }

    const result = await apiResponse.json();
    console.log('Response success:', result); // Debug success
    return result;
  },
});

const fieldLabels = {
  company_name: 'Company Name',
  contact_person: 'Contact Person',
  email: 'Email',
  phone: 'Phone',
  address: 'Address',
  city: 'City',
  postal_code: 'Postal Code',
  country: 'Country',
  product_name: 'Product Name',
  product_code: 'Product Code',
  serial_number: 'Serial Number',
  purchase_date: 'Purchase Date',
  delivery_note_number: 'Delivery Note Number',
  description: 'Description',
};

const fieldIcons = {
  company_name: <Business />,
  contact_person: <Person />,
  email: <Email />,
  phone: <Phone />,
  address: <LocationOn />,
  city: <LocationOn />,
  postal_code: <LocationOn />,
  country: <LocationOn />,
  product_name: <Inventory />,
  product_code: <Category />,
  serial_number: <CheckCircle />,
  purchase_date: <Today />,
  delivery_note_number: <Assignment />,
  description: <Description />,
};

// Field Issue Card Component
const FieldIssueCard = ({ issue, onRespond }) => {
  const [responseText, setResponseText] = useState('');
  const [showResponse, setShowResponse] = useState(false);

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'low':
        return 'success';
      case 'medium':
        return 'warning';
      case 'high':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleSubmitResponse = () => {
    if (responseText.trim()) {
      onRespond(issue.id, responseText.trim());
      setResponseText('');
      setShowResponse(false);
    }
  };

  // Handle missing or inconsistent description
  const issueDescription = issue.description || issue.issue_description || 'No description provided by serviceman';

  return (
    <Accordion key={issue.id} sx={{ mb: 2 }}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {fieldIcons[issue.field_name]}
            <Typography variant="subtitle1">{fieldLabels[issue.field_name]}</Typography>
          </Box>
          <Chip size="small" label={issue.severity.toUpperCase()} color={getSeverityColor(issue.severity)} />
          {issue.resolved && <Chip size="small" label="Resolved" color="success" icon={<CheckCircle />} />}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ display: 'block' }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Issue Description:
          </Typography>
          <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
            {issueDescription}
          </Typography>
        </Box>

        {issue.customer_response && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Your Response:
            </Typography>
            <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
              {issue.customer_response}
            </Typography>
          </Box>
        )}

        {!issue.resolved && !issue.customer_response && (
          <Button
            variant="outlined"
            startIcon={<Comment />}
            onClick={() => setShowResponse(!showResponse)}
          >
            Respond
          </Button>
        )}

        {showResponse && (
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={responseText}
              onChange={(e) => setResponseText(e.target.value)}
              placeholder="Enter your response..."
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleSubmitResponse}
                disabled={!responseText.trim()}
              >
                Submit Response
              </Button>
              <Button
                variant="outlined"
                onClick={() => setShowResponse(false)}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

const CustomerRMADetailsView = ({ rmaId, onBack }) => {
  const auth = useAuth();
  const [rmaData, setRmaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const apiService = createApiService(auth);

  const loadRMADetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getRMADetails(rmaId);
      setRmaData({
        ...data,
        field_issues: Array.isArray(data.field_issues)
          ? data.field_issues.map(issue => ({
              ...issue,
              description: issue.issue_description || issue.description || null,
            }))
          : [],
      });
    } catch (err) {
      setError(err.message);
      console.error('Error loading RMA details:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (rmaId) {
      loadRMADetails();
    }
  }, [rmaId]);
const handleDownloadAttachment = async (attachmentId, filename) => {
  try {
    const result = await apiService.downloadAttachment(attachmentId);

    if (result.type === 'redirect') {
      const a = document.createElement('a');
      a.href = result.url;
      a.download = result.filename || filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else {
      const url = window.URL.createObjectURL(result.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    }

    setSnackbarMessage('Attachment downloaded successfully');
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  } catch (err) {
    setSnackbarMessage('Error downloading attachment: ' + err.message);
    setSnackbarSeverity('error');
    setSnackbarOpen(true);
  }
};

  const handleExportRMA = async () => {
    try {
      const blob = await apiService.exportRMA(rmaId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${rmaData.rma_number}-export.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setSnackbarMessage('RMA exported successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      setSnackbarMessage('Error exporting RMA: ' + err.message);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleRespondToIssue = async (issueId, response) => {
    try {
      await apiService.respondToFieldIssue(issueId, response);
      setRmaData(prev => ({
        ...prev,
        field_issues: prev.field_issues.map(issue =>
          issue.id === issueId ? { ...issue, customer_response: response } : issue
        ),
      }));
      setSnackbarMessage('Response submitted successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarMessage('Error submitting response: ' + error.message);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'submitted':
        return 'primary';
      case 'under_review':
        return 'warning';
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      case 'needs_info':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    return dateString ? new Date(dateString).toLocaleDateString() : 'N/A';
  };

  const formatDateTime = (dateString) => {
    return dateString ? new Date(dateString).toLocaleString() : 'N/A';
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={loadRMADetails}>
          Retry
        </Button>
      </Box>
    );
  }

  if (!rmaData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          No RMA data available
        </Alert>
      </Box>
    );
  }

  const unresolvedIssues = rmaData.field_issues?.filter(issue => !issue.resolved) || [];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={onBack}
          >
            Back to List
          </Button>
          <Typography variant="h5">
            {rmaData.rma_number}
          </Typography>
          <Chip
            label={rmaData.status?.replace('_', ' ').toUpperCase()}
            color={getStatusColor(rmaData.status)}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadRMADetails}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleExportRMA}
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* Issues Alert */}
      {unresolvedIssues.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            This RMA has {unresolvedIssues.length} unresolved field issue(s) that need your attention. You can respond to these issues below.
          </Typography>
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Company Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Company Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {['company_name', 'contact_person', 'email', 'phone'].map((field) => (
                  <Box key={field} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {fieldIcons[field]}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {field.replace('_', ' ').toUpperCase()}
                      </Typography>
                      <Typography variant="body2">
                        {rmaData[field] || 'N/A'}
                      </Typography>
                    </Box>
                    {rmaData.field_issues?.some(issue => issue.field_name === field && !issue.resolved) && (
                      <Tooltip title="This field has unresolved issues">
                        <Warning color="error" />
                      </Tooltip>
                    )}
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Address Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Address Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {['address', 'city', 'postal_code', 'country'].map((field) => (
                  <Box key={field} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {fieldIcons[field]}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {field.replace('_', ' ').toUpperCase()}
                      </Typography>
                      <Typography variant="body2">
                        {rmaData[field] || 'N/A'}
                      </Typography>
                    </Box>
                    {rmaData.field_issues?.some(issue => issue.field_name === field && !issue.resolved) && (
                      <Tooltip title="This field has unresolved issues">
                        <Warning color="error" />
                      </Tooltip>
                    )}
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Product Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Product Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {['product_name', 'product_code', 'serial_number', 'purchase_date', 'delivery_note_number'].map((field) => (
                  <Box key={field} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {fieldIcons[field]}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {field.replace('_', ' ').toUpperCase()}
                      </Typography>
                      <Typography variant="body2">
                        {field === 'purchase_date' && rmaData[field]
                          ? formatDate(rmaData[field])
                          : rmaData[field] || 'N/A'}
                      </Typography>
                    </Box>
                    {rmaData.field_issues?.some(issue => issue.field_name === field && !issue.resolved) && (
                      <Tooltip title="This field has unresolved issues">
                        <Warning color="error" />
                      </Tooltip>
                    )}
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Attachments */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Attachments ({rmaData.attachments?.length || 0})
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {rmaData.attachments?.length > 0 ? (
                <List dense>
                  {rmaData.attachments.map((attachment) => (
                    <ListItem key={attachment.id}>
                      <ListItemIcon>
                        <AttachFile />
                      </ListItemIcon>
                      <ListItemText
                        primary={attachment.original_filename}
                        secondary={`${formatFileSize(attachment.file_size)} • ${formatDateTime(attachment.uploaded_at)}`}
                      />
                      <IconButton
                        color="primary"
                        onClick={() => handleDownloadAttachment(attachment.id, attachment.original_filename)}
                      >
                        <Download />
                      </IconButton>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No attachments available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Description */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Problem Description
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="body2">
                  {rmaData.description || 'No description provided'}
                </Typography>
              </Paper>
            </CardContent>
          </Card>
        </Grid>

        {/* Field Issues */}
        {rmaData.field_issues?.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Field Issues ({rmaData.field_issues.length})
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {unresolvedIssues.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" color="error" gutterBottom>
                      Unresolved Issues ({unresolvedIssues.length})
                    </Typography>
                    {unresolvedIssues.map((issue) => (
                      <FieldIssueCard
                        key={issue.id}
                        issue={issue}
                        onRespond={handleRespondToIssue}
                      />
                    ))}
                  </Box>
                )}
                {rmaData.field_issues.filter(issue => issue.resolved).length > 0 && (
                  <Box>
                    <Typography variant="h6" color="success.main" gutterBottom>
                      Resolved Issues ({rmaData.field_issues.filter(issue => issue.resolved).length})
                    </Typography>
                    {rmaData.field_issues
                      .filter(issue => issue.resolved)
                      .map((issue) => (
                        <FieldIssueCard
                          key={issue.id}
                          issue={issue}
                          onRespond={handleRespondToIssue}
                        />
                      ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Timestamps */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Timeline
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {rmaData.submitted_at && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      SUBMITTED
                    </Typography>
                    <Typography variant="body2">
                      {formatDateTime(rmaData.submitted_at)}
                    </Typography>
                  </Box>
                )}
                {rmaData.created_at && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      CREATED
                    </Typography>
                    <Typography variant="body2">
                      {formatDateTime(rmaData.created_at)}
                    </Typography>
                  </Box>
                )}
                {rmaData.updated_at && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      LAST UPDATED
                    </Typography>
                    <Typography variant="body2">
                      {formatDateTime(rmaData.updated_at)}
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomerRMADetailsView;